import React, { useState, useEffect } from 'react';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Flex,
  VStack,
  Link as ChakraLink,
  Icon,
  Button,
  Text,
  Avatar,
  Divider,
  Badge,
  IconButton,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FiUsers, FiTag, FiLogOut, FiShield, FiBook, FiKey, FiDatabase, FiServer, FiSearch, FiTrendingUp, FiHeart, FiLayout, FiActivity, FiWifi, FiMenu, FiSend } from 'react-icons/fi';

import Users from './components/Users';
import RundownStatus from './components/RundownStatus';
import QueuedCommunications from './components/QueuedCommunications';
import WebSocketStatus from './components/WebSocketStatus';
import ProfileTags from './components/ProfileTags';
import SuperAdmin from './components/SuperAdmin';
import ApiDocs from './components/ApiDocs';
import ApiKeys from './components/ApiKeys';
import RedisStatus from './components/RedisStatus';
import PostgresStatus from './components/PostgresStatus';
import ElasticStatus from './components/ElasticStatus';
import UsageAnalytics from './components/UsageAnalytics';
import HealthCheck from './components/HealthCheck';
import PromoSlots from './components/PromoSlots';

import LoginForm from './components/Login';

const NavButton = ({ to, icon, children, isActive }) => (
  <ChakraLink as={RouterLink} to={to} _hover={{ textDecoration: 'none' }}>
    <Button
      leftIcon={<Icon as={icon} boxSize={5} />}
      width="full"
      justifyContent="flex-start"
      variant={isActive ? 'solid' : 'ghost'}
      colorScheme={isActive ? 'orange' : 'whiteAlpha'}
      color={isActive ? 'white' : 'whiteAlpha.800'}
      bg={isActive ? 'whiteAlpha.200' : 'transparent'}
      _hover={{
        bg: isActive ? 'whiteAlpha.300' : 'whiteAlpha.100',
        color: 'white',
      }}
      borderRadius="lg"
      py={6}
      fontSize="md"
      fontWeight={isActive ? '600' : '400'}
    >
      {children}
    </Button>
  </ChakraLink>
);

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  useEffect(() => { onClose(); }, [location.pathname, onClose]);
  
  const instanceName = localStorage.getItem('instance_name') || 'Instance';
  const userName = localStorage.getItem('user_name') || 'Admin';

  const handleLogin = () => {
    setToken(localStorage.getItem('token'));
    navigate('/');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('instance_id');
    localStorage.removeItem('instance_name');
    localStorage.removeItem('user_name');
    setToken(null);
  };

  if (!token) {
    return <LoginForm onLogin={handleLogin} />;
  }

  const renderSidebarContent = () => (
    <>
      {/* Logo/Brand */}
      <Box px={4} pt={2} pb={2}>
        <Flex align="center" px={4} mb={6}>
          <Box w={12} h={12} borderRadius="full" overflow="hidden" bg="white" p={0.5}>
            <img src="/static/selwyn-logo.png" alt="Selwyn" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
          </Box>
          <Box ml={3}>
            <Text fontSize="lg" fontWeight="bold" color="white">API Admin</Text>
            <Badge colorScheme="orange" variant="subtle" fontSize="xs">{instanceName}</Badge>
          </Box>
        </Flex>
        <Divider borderColor="whiteAlpha.300" />
      </Box>

      {/* Scrollable nav links */}
      <VStack
        flex="1"
        overflowY="auto"
        spacing={2}
        align="stretch"
        px={4}
        py={2}
        sx={{
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { bg: 'transparent' },
          '&::-webkit-scrollbar-thumb': { bg: 'whiteAlpha.300', borderRadius: 'full' },
        }}
      >
        <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mb={2}>
          User Management
        </Text>
        <NavButton to="/" icon={FiUsers} isActive={location.pathname === '/'}>
          Users & Profiles
        </NavButton>
        <NavButton to="/profile-tags" icon={FiTag} isActive={location.pathname === '/profile-tags'}>
          Profile Tags
        </NavButton>

        <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
          Administration
        </Text>
        <NavButton to="/super-admin" icon={FiShield} isActive={location.pathname === '/super-admin'}>
          Super Admin
        </NavButton>
        <NavButton to="/api-keys" icon={FiKey} isActive={location.pathname === '/api-keys'}>
          API Keys
        </NavButton>
        <NavButton to="/promo-slots" icon={FiLayout} isActive={location.pathname === '/promo-slots'}>
          Promo Slots
        </NavButton>

        <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
          Insights
        </Text>
        <NavButton to="/analytics" icon={FiTrendingUp} isActive={location.pathname === '/analytics'}>
          Usage Analytics
        </NavButton>
        <NavButton to="/health" icon={FiHeart} isActive={location.pathname === '/health'}>
          Health Check
        </NavButton>
        <NavButton to="/rundown-status" icon={FiActivity} isActive={location.pathname === '/rundown-status'}>
          Rundown Status
        </NavButton>
        <NavButton to="/queued-communications" icon={FiSend} isActive={location.pathname === '/queued-communications'}>
          Communications Queue
        </NavButton>

        <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
          Infrastructure
        </Text>
        <NavButton to="/redis" icon={FiDatabase} isActive={location.pathname === '/redis'}>
          Redis Status
        </NavButton>
        <NavButton to="/postgres" icon={FiServer} isActive={location.pathname === '/postgres'}>
          PostgreSQL Status
        </NavButton>
        <NavButton to="/elasticsearch" icon={FiSearch} isActive={location.pathname === '/elasticsearch'}>
          Elasticsearch Status
        </NavButton>
        <NavButton to="/websockets" icon={FiWifi} isActive={location.pathname === '/websockets'}>
          WebSocket Server
        </NavButton>

        <Text px={4} fontSize="xs" color="whiteAlpha.600" fontWeight="semibold" textTransform="uppercase" mt={6} mb={2}>
          Developer
        </Text>
        <NavButton to="/api" icon={FiBook} isActive={location.pathname === '/api'}>
          API Documentation
        </NavButton>
      </VStack>

      {/* User Section */}
      <Box px={4} pb={4} pt={2} flexShrink={0}>
        <Divider borderColor="whiteAlpha.300" mb={2} />
        <Flex align="center" px={4} py={3}>
          <Avatar size="sm" bg="orange.400" name={userName} />
          <Box ml={3} flex={1}>
            <Text fontSize="sm" color="white" fontWeight="medium">{userName}</Text>
            <Text fontSize="xs" color="whiteAlpha.600">Super Admin</Text>
          </Box>
        </Flex>
        <Button
          leftIcon={<Icon as={FiLogOut} />}
          onClick={handleLogout}
          width="full"
          justifyContent="flex-start"
          variant="ghost"
          color="whiteAlpha.800"
          _hover={{ bg: 'whiteAlpha.100', color: 'white' }}
          borderRadius="lg"
        >
          Logout
        </Button>
      </Box>
    </>
  );

  return (
    <Flex direction="column" height="100vh" bg="gray.50">
      {/* Mobile top bar */}
      <Flex
        display={{ base: 'flex', md: 'none' }}
        flexShrink={0}
        height="56px"
        bg="orange.700"
        align="center"
        px={4}
        justify="space-between"
        boxShadow="md"
      >
        <IconButton
          icon={<Icon as={FiMenu} />}
          onClick={onOpen}
          variant="ghost"
          color="white"
          aria-label="Open navigation"
          _hover={{ bg: 'whiteAlpha.200' }}
        />
        <Text color="white" fontWeight="bold" fontSize="md">API Admin</Text>
        <IconButton
          icon={<Icon as={FiLogOut} />}
          onClick={handleLogout}
          variant="ghost"
          color="white"
          aria-label="Logout"
          _hover={{ bg: 'whiteAlpha.200' }}
        />
      </Flex>

      {/* Desktop layout row */}
      <Flex flex="1" overflow="hidden">
        {/* Desktop Sidebar */}
        <Flex
          as="nav"
          direction="column"
          display={{ base: 'none', md: 'flex' }}
          width="280px"
          bgGradient="linear(to-b, orange.600, orange.800)"
          boxShadow="xl"
          flexShrink={0}
        >
          {renderSidebarContent()}
        </Flex>

        {/* Main Content */}
        <Box flex="1" p={{ base: 4, md: 8 }} overflowY="auto">
          <Routes>
            <Route path="/" element={<Users />} />
            <Route path="/profile-tags" element={<ProfileTags />} />
            <Route path="/super-admin" element={<SuperAdmin />} />
            <Route path="/api-keys" element={<ApiKeys />} />
            <Route path="/redis" element={<RedisStatus />} />
            <Route path="/postgres" element={<PostgresStatus />} />
            <Route path="/elasticsearch" element={<ElasticStatus />} />
            <Route path="/analytics" element={<UsageAnalytics />} />
            <Route path="/health" element={<HealthCheck />} />
            <Route path="/rundown-status" element={<RundownStatus />} />
            <Route path="/queued-communications" element={<QueuedCommunications />} />
            <Route path="/websockets" element={<WebSocketStatus />} />
            <Route path="/promo-slots" element={<PromoSlots />} />
            <Route path="/api" element={<ApiDocs />} />
          </Routes>
        </Box>
      </Flex>

      {/* Mobile nav drawer */}
      <Drawer isOpen={isOpen} onClose={onClose} placement="left">
        <DrawerOverlay />
        <DrawerContent bgGradient="linear(to-b, orange.600, orange.800)" maxW="280px">
          <DrawerCloseButton color="white" top={4} />
          <DrawerBody p={0}>
            <Flex direction="column" minH="100%">
              {renderSidebarContent()}
            </Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Flex>
  );
}

export default App;
